import React, { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { ArticleMainInfo } from './ArticleMainInfo'
import { ArticleHeader } from './ArticleHeader'
import { ArticleMeta } from './ArticleMeta'
import { LightGrayDivider } from '@obeta/components/lib/light-gray-divider/LightGrayDivider'
import { TechnicalSpecifications } from './TechnicalSpecifications'
import { StickyHeader } from './StickyHeader'
import { ArticleDescription } from './ArticleDescription'
import { ArticleAccessories } from './ArticleAccessories'
import { ArticleDownloads } from './ArticleDownloads'
import { useUserSelectedStore } from '@obeta/data/lib/hooks/useUserSelectedStore'
import { ProductsCarousel } from './ProductsCarousel'
import { ArticleCategories } from './ArticleCategories'
import { ProductProvider } from './ProductProvider'
import { SectionProvider, SectionWrapper } from './SectionProvider'
import { useBreakpoints } from '@obeta/data/lib/hooks/useBreakpoints'
import { useArticleDetails } from '@obeta/data/lib/hooks/useArticleDetailsData'
import { Box, Typography } from '@mui/material'
import { ProductAggregate } from '@obeta/models/lib/models/Article/Shop/Product'
import { useParams } from '@obeta/data/lib/hooks/useHistoryApi'
import { trackCustom } from '@obeta/utils/lib/tracking'
import { ArticleStickyHeaderProvider } from './wrappers/ArticleStickyHeaderProvider'
import { useOxomiIntegration } from '@obeta/data/lib/hooks/useOxomiIntegration'
import { useTranslation } from 'react-i18next'
import { waitForField, waitForResolve } from '@obeta/utils/lib/waitFor'
import { OxomiGlobal } from '@obeta/models/lib/models/Oxomi'
import { ArticleDetailsSections } from '@obeta/utils/lib/variables'
import oxomiCatalogStyles from '@obeta/components/lib/oxomi/OxomiCatalogs.module.scss'
import { useAppDomain } from '@obeta/data/lib/hooks/useAppDomain'
import { ShopDomainToSupplierNumber } from '@obeta/utils/lib/domains'
import styles from './ArticleDetailsPage.module.scss'
import { useCrossSellingProducts } from '@obeta/data/lib/hooks/product/useCrossSellingProducts'
import { useUpSellingProducts } from '@obeta/data/lib/hooks/product/useUpSellingProducts'
import { CarouselFilter } from '@obeta/models/lib/models/UI'
import { Carousel } from './Carousel'
import { ArticleCarouselCard } from '@obeta/components/lib/product-card'
import { DataAttributesProvider } from '@obeta/data/lib/hooks/useDataAttributes'
import { useSelectedCart } from '@obeta/data/lib/hooks/useSelectedCart'
import { useEntities } from '@obeta/data/lib/hooks'
import { ShoppingCartV2 } from '@obeta/models/lib/models'
import { useUserDataV2 } from '@obeta/data/lib/hooks/useUserDataV2'
import { useVoltimumPoints } from '@obeta/data/lib/hooks/useVoltimumPoints'
import { ProductCollectionActionProvider } from '@obeta/data/lib/hooks/useProductCollectionActions'
import { getDeliveryDaysTranslation, getTrackingOptions } from '@obeta/utils/lib/product'

export const ArticleHeaderWrap: React.FC<{
  article: ProductAggregate | undefined
  mobile: boolean
  desktop: boolean
  titleClassName?: string
}> = ({ article, mobile, desktop, titleClassName }) => {
  return (
    <div className={clsx(styles.header)}>
      <ArticleHeader mobile={mobile} className={titleClassName} />
      {mobile && (
        <Box className={titleClassName}>
          <LightGrayDivider />
        </Box>
      )}
      <ArticleMeta article={article} desktop={desktop} titleClassName={titleClassName} />
    </div>
  )
}

export const ArticleDetailPage: React.FC = () => {
  const domain = useAppDomain()
  const article = useArticleDetails()
  const { mobile, tabletAll, tabletWide, tablet, desktop } = useBreakpoints()
  const carts = useEntities<ShoppingCartV2>('cartsv2')
  const p = useParams()
  const { id: articleId } = p
  const selectedCart = useSelectedCart()
  const { selectedStore } = useUserSelectedStore()
  const { t } = useTranslation()
  const { isLoggedIn, user } = useUserDataV2()

  const oxomiCatalogContainerRef = useRef<HTMLDivElement | null>(null)

  useOxomiIntegration()

  const [hasCatalogData, setHasCatalogData] = useState(false)

  // ###################### //
  // CROSS SELLING PRODUCTS //
  // ###################### //

  const [selectedCrossSellingCategory, setSelectedCrossSellingCategory] = useState<string>('ALL')
  const { crossSellingCategories, crossSellingProducts, isLoadingCrossSellingProducts } =
    useCrossSellingProducts(articleId, selectedCrossSellingCategory)
  const [crossSellingFilters, setCrossSellingFilters] = useState<CarouselFilter[]>([])

  const { sapIds: crossSellingSapIds } = useVoltimumPoints(
    crossSellingProducts
      .filter((product) => Boolean(product?.sapId))
      .map((product) => ({ sapId: product.sapId, amount: 1 }))
  )

  const onChangeRecommendationFilter = useCallback((id: string, value: number | string) => {
    if (id === 'deliveryTimes') {
      setSelectedUpSellingDeliveryTime(value as number)
    } else {
      setSelectedUpSellingSupplier(value as string)
    }
  }, [])

  useEffect(() => {
    if (crossSellingCategories && crossSellingCategories?.length > 0) {
      setCrossSellingFilters([
        {
          id: 'categories',
          options: [
            {
              label: t('COMMON.ALL_UPPER_CASE'),
              value: 'ALL',
            },
            ...(crossSellingCategories ?? []).map((category) => ({
              label: category.name,
              value: category.name,
            })),
          ],
          title: t('COMMON.CATEGORIES'),
          value: selectedCrossSellingCategory ?? 'ALL',
        },
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossSellingCategories])

  // ################### //
  // UP SELLING PRODUCTS //
  // ################### //

  const [selectedUpSellingDeliveryTime, setSelectedUpSellingDeliveryTime] = useState<number>(-1)

  const [selectedUpSellingSupplier, setSelectedUpSellingSupplier] = useState<string>('ALL')
  const {
    isLoadingUpSellingProducts,
    upSellingDeliveryTimes,
    upSellingProducts,
    upSellingSuppliers,
  } = useUpSellingProducts(articleId, {
    maxDeliveryTime: selectedUpSellingDeliveryTime,
    supplier: selectedUpSellingSupplier,
  })
  const [upSellingFilters, setUpSellingFilters] = useState<CarouselFilter[]>([])

  const { sapIds: upSellingSapIds } = useVoltimumPoints(
    upSellingProducts
      .filter((product) => Boolean(product?.sapId))
      .map((product) => ({ sapId: product.sapId, amount: 1 }))
  )

  useEffect(() => {
    if (
      upSellingDeliveryTimes &&
      upSellingDeliveryTimes?.length > 0 &&
      upSellingSuppliers &&
      upSellingSuppliers.length > 0
    ) {
      setUpSellingFilters([
        {
          id: 'suppliers',
          options: [
            {
              label: t('COMMON.ALL_UPPER_CASE'),
              value: 'ALL',
            },
            ...(upSellingSuppliers ?? []).map((supplier) => ({
              label: supplier.name,
              value: supplier.name,
            })),
          ],
          title: t('COMMON.SUPPLIERS'),
          value: selectedUpSellingSupplier ?? 'ALL',
        },
        {
          id: 'deliveryTimes',
          options: [
            {
              label: t('COMMON.ALL_UPPER_CASE'),
              value: -1,
            },
            ...(upSellingDeliveryTimes ?? []).map((deliveryTime) => ({
              label: t(getDeliveryDaysTranslation(deliveryTime.max_delivery_days), {
                count: deliveryTime.max_delivery_days,
              }),
              value: deliveryTime.max_delivery_days,
            })),
          ],
          title: t('COMMON.DELIVERY_TIME'),
          value: selectedUpSellingDeliveryTime ?? -1,
        },
      ])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upSellingSuppliers])

  const oxomiInstance = window?.oxomi
  useEffect(() => {
    /*
    Todo: the article detailpage has no context and it loads a lot of data (meta, price, oxomi)
    therfore any kind of scroll restoration i doomed to fail is the content and the scroll length will never match
    and you end up somwhere undetermined when navigating back and forth
    therefore i added this to at least be at a defined position when navigating between articles
    should we make this properly scroll restoratbale as well? if so we should cache the responses
    */
    window.scrollTo({ top: 0 })
  }, [articleId])

  useEffect(() => {
    const fetchOxomiResponse = async () => {
      try {
        if (!article || !oxomiInstance) {
          return
        }
        const supplierId = article?.supplierId as string
        const supplierIdNumber = supplierId.startsWith('L') ? supplierId.slice(1) : supplierId
        const oxomi = await waitForField<OxomiGlobal>({ field: 'oxomi' })
        await waitForResolve({ callback: oxomi.isInitialized })
        const shopSpecificSupplierNumber = ShopDomainToSupplierNumber[domain]

        const hasCatalogData = await oxomiInstance.productDataExists({
          products: [
            { itemNumber: article?.dehaId, supplierNumber: shopSpecificSupplierNumber },
            { itemNumber: article?.oxomiId, supplierNumber: supplierIdNumber },
          ],
          information: ['pages'],
        })

        // TODO: Replace hardcoded mapping with dynamic supplier number from article API (once available)
        // Currently all domains map to 2901 (Obeta), but this will be shop-specific in the future
        if (hasCatalogData.pages) {
          setHasCatalogData(true)
          oxomiInstance.productData({
            products: [
              { itemNumber: article?.dehaId, supplierNumber: shopSpecificSupplierNumber },
              { itemNumber: article?.oxomiId, supplierNumber: supplierIdNumber },
            ],
            information: {
              pages: {
                target: '#oxomi-catalog-target_page',
                showActions: true,
              },
            },
          })
        }
      } catch (e) {
        trackCustom('oxomi-catalog-item-pages-with-download-error', {
          error: e,
        })
      }
    }

    setHasCatalogData(false)
    fetchOxomiResponse()
  }, [domain, article, articleId, oxomiInstance])

  return (
    <ArticleStickyHeaderProvider>
      <ProductProvider product={article}>
        <SectionProvider typographyHeaderVariant={desktop ? 'headline3Bold' : 'headline4Bold'}>
          <div className={styles.root}>
            <div className={clsx(styles.row, { [styles.rightOverflowing]: mobile })}>
              <ArticleHeaderWrap
                article={article}
                desktop={desktop || tabletWide}
                mobile={mobile}
                titleClassName={styles.titleSpacing}
              />
              {!mobile && <LightGrayDivider />}
              <ArticleMainInfo
                selectedStore={selectedStore}
                article={article}
                defaultStoreId={selectedStore?.id}
                noRightOverFlowClass={styles.titleSpacing}
              />
            </div>
            {(desktop || tabletWide) && <StickyHeader />}
            <TechnicalSpecifications article={article} />
            {Boolean(article?.articleDescription ?? article?.catalogDescription) && (
              <ArticleDescription
                description={article?.articleDescription ?? ''}
                catalogDescription={article?.catalogDescription ?? ''}
              />
            )}
            <Box className={clsx(styles.row, { [styles.rightOverflowing]: mobile })}>
              <ArticleAccessories
                selectedStore={selectedStore}
                mobile={mobile}
                tabletAll={tabletAll}
              />
            </Box>
            <ArticleDownloads article={article} />

            <Box className={clsx(styles.row, { [styles.rightOverflowing]: mobile })}>
              <ProductsCarousel
                type="recommendations"
                mobile={mobile}
                tabletAll={tabletAll}
                selectedStore={selectedStore}
                tablet={tablet}
              />
            </Box>

            <Box className={clsx(styles.row, { [styles.rightOverflowing]: mobile })}>
              <ArticleCategories article={article} titleClassName={styles.titleSpacing} />
            </Box>
            <SectionWrapper available={hasCatalogData} id={ArticleDetailsSections.OxomiCatalog}>
              <Box className={clsx(styles.row)}>
                <Typography variant={!desktop ? 'headline4Bold' : 'headline3Bold'}>
                  {t('ARTICLE_DETAIL.OXOMI_CATALOG')}
                </Typography>
                <div
                  className={clsx(
                    oxomiCatalogStyles.outerGrid,
                    oxomiCatalogStyles.noPadding,
                    oxomiCatalogStyles.marginTopOneRem
                  )}
                >
                  <div
                    ref={oxomiCatalogContainerRef}
                    id="oxomi-catalog-target_page"
                    className={oxomiCatalogStyles.oxomiCatalogTarget}
                  ></div>
                </div>
              </Box>
            </SectionWrapper>

            <Box
              className={clsx(styles.row, styles.coloredBackground, {
                [styles.rightOverflowing]: mobile,
              })}
            >
              <ProductsCarousel
                titleClassName={styles.titleSpacing}
                type="alternative"
                tablet={tablet}
                mobile={mobile}
                tabletAll={tabletAll}
                selectedStore={selectedStore}
                withDataAttributes
              />
              {crossSellingFilters.length > 0 && (
                <ProductCollectionActionProvider value={getTrackingOptions('crossselling')}>
                  <Carousel
                    cardRenderer={(index) => {
                      const crossSellingProduct = crossSellingProducts[index]

                      return (
                        <DataAttributesProvider
                          value={{
                            'data-track-id': 'associatedProduct',
                            'data-product-id': crossSellingProduct.sapId,
                          }}
                        >
                          <ArticleCarouselCard
                            key={crossSellingProduct.sapId}
                            carts={carts}
                            isLoggedIn={isLoggedIn}
                            mobile={mobile}
                            product={crossSellingProduct}
                            productId={crossSellingProduct.sapId}
                            selectedCart={selectedCart}
                            selectedStore={selectedStore}
                            showVoltimumLabel={crossSellingSapIds?.includes(p.sapId)}
                            tablet={tabletAll}
                            user={user}
                            withDataAttributes
                          />
                        </DataAttributesProvider>
                      )
                    }}
                    isLoading={isLoadingCrossSellingProducts}
                    itemCount={crossSellingProducts.length}
                    filters={crossSellingFilters}
                    skeletonClassName={styles.recommendationSkeletons}
                    title={t('ARTICLE_DETAIL.CROSS_SELLING')}
                    onCancelMobileFilters={() => setSelectedCrossSellingCategory('ALL')}
                    onChangeFilter={onChangeRecommendationFilter}
                    onEnterViewport={() => trackCustom('visible-recommendation-cross-selling')}
                    onRenderItems={() => trackCustom('rendered-recommendation-cross-selling')}
                  />
                </ProductCollectionActionProvider>
              )}
              {upSellingFilters.length > 0 && (
                <ProductCollectionActionProvider value={getTrackingOptions('upselling')}>
                  <Carousel
                    cardRenderer={(index) => {
                      const upSellingProduct = upSellingProducts[index]

                      return (
                        <DataAttributesProvider
                          value={{
                            'data-track-id': 'associatedProduct',
                            'data-product-id': upSellingProduct.sapId,
                          }}
                        >
                          <ArticleCarouselCard
                            key={upSellingProduct.sapId}
                            carts={carts}
                            isLoggedIn={isLoggedIn}
                            mobile={mobile}
                            product={upSellingProduct}
                            productId={upSellingProduct.sapId}
                            selectedCart={selectedCart}
                            selectedStore={selectedStore}
                            showVoltimumLabel={upSellingSapIds?.includes(p.sapId)}
                            tablet={tabletAll}
                            user={user}
                            withDataAttributes
                          />
                        </DataAttributesProvider>
                      )
                    }}
                    isLoading={isLoadingUpSellingProducts}
                    itemCount={upSellingProducts.length}
                    filters={upSellingFilters}
                    skeletonClassName={styles.recommendationSkeletons}
                    title={t('ARTICLE_DETAIL.UP_SELLING.TITLE')}
                    onCancelMobileFilters={() => {
                      setSelectedUpSellingDeliveryTime(-1)
                      setSelectedUpSellingSupplier('ALL')
                    }}
                    onChangeFilter={onChangeRecommendationFilter}
                    onEnterViewport={() => trackCustom('visible-recommendation-up-selling')}
                    onRenderItems={() => trackCustom('rendered-recommendation-up-selling')}
                  />
                </ProductCollectionActionProvider>
              )}
            </Box>
          </div>
        </SectionProvider>
      </ProductProvider>
    </ArticleStickyHeaderProvider>
  )
}
